import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import { cn } from '@/lib/utils';
import { ColumnFilterValue, matchesColumnFilter } from '@/lib/table-query';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table as TableInstance,
  getExpandedRowModel,
  ColumnPinningState,
  getGroupedRowModel,
  Column,
  Row,
  Cell,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui-kit/empty';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { DataTablePagination } from '@/components/core';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Button } from '@/components/ui-kit/button';
import { useIsMobile } from '@/hooks/use-media-query';
import { DENSITY_CELL_CLASS, useTableDensity } from '@/hooks/use-table-density';
import { DataCardList } from './data-card-list';
import {
  initialColumnVisibility,
  storeColumnVisibility,
} from '../columns-menu/columns-menu';

const DEFAULT_COLUMN_PINNING_CONFIG: ColumnPinningState = { left: ['select'], right: [] };

/**
 * HR Module Prototype `.tcard`: a white card (border, lg radius, small shadow) around the toolbar, rows and
 * pager. `overflow-clip` (not hidden) keeps the sticky header working. Inside a card, dialog or sheet the
 * table is flat (`.card .tcard`).
 */
export const TABLE_CARD_CLASS = cn(
  // `shadow` is Tailwind 3's equivalent of the prototype's --shadow-sm.
  'flex w-full flex-col overflow-clip rounded-lg border bg-card shadow',
  '[[data-slot=card]_&]:rounded-none [[data-slot=card]_&]:border-0 [[data-slot=card]_&]:shadow-none [[data-slot=card]_&]:bg-transparent',
  '[[data-slot=dialog-content]_&]:rounded-none [[data-slot=dialog-content]_&]:border-0 [[data-slot=dialog-content]_&]:shadow-none [[data-slot=dialog-content]_&]:bg-transparent',
  '[[data-slot=sheet-content]_&]:rounded-none [[data-slot=sheet-content]_&]:border-0 [[data-slot=sheet-content]_&]:shadow-none [[data-slot=sheet-content]_&]:bg-transparent'
);

/** A number, a percentage or a duration such as "8.5", "1,250", "75%", "-3", "12 h" (Component Map ›
 * Tables: numeric columns are detected, right-aligned and use tabular figures). */
const NUMERIC_TEXT = /^[-+]?\d[\d,]*(\.\d+)?\s*(%|h|d|days?|hours?)?$/i;
const isNumericValue = (value: unknown) =>
  typeof value === 'number' || (typeof value === 'string' && NUMERIC_TEXT.test(value.trim()));

/** Whether the table is wider than its frame, kept up to date as either changes size. */
const useHorizontalOverflow = (table: HTMLTableElement | null) => {
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const frame = table?.parentElement;
    if (!table || !frame || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setOverflows(table.scrollWidth > frame.clientWidth + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    observer.observe(table);
    return () => observer.disconnect();
  }, [table]);
  return overflows;
};

interface AdvanceDataTableProps<TData, TValue> {
  /**
   * Defines the column structure of the table.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/core/column-def)
   */
  columns: ColumnDef<TData, TValue>[];
  /**
   * The dataset to be displayed in the table.
   */
  data: TData[];
  /**
   * Callback function triggered when a row is clicked.
   */
  onRowClick?: (data: TData) => void;
  /**
   * Displays a loading state when `true`.
   */
  isLoading?: boolean;
  /**
   * Displays an error message if provided.
   */
  error?: Error | null;
  /**
   * Function to render a custom column toolbar.
   * @param table - The table instance providing access to table state and methods.
   * @returns A React node to be rendered as part of the toolbar.
   */
  columnsToolbar?: (table: TableInstance<TData>) => React.ReactNode;
  /**
   * Function to render a custom filter toolbar.
   * @param table - The table instance providing access to table state and methods.
   * @returns A React node to be rendered as part of the filter toolbar.
   */
  filterToolbar?: (table: TableInstance<TData>) => React.ReactNode;
  /**
   * Enables expandable row content when `true`.
   */
  isExpandRowContent?: boolean;
  /**
   * Function to render expandable row content.
   * @param rowId - The unique identifier of the row being expanded.
   * @param colSpan - The number of columns spanned by the expanded content.
   * @returns A React node to be rendered as the expanded row content.
   */
  expandRowContent?: (rowId: string, colSpan: number) => React.ReactNode;
  /**
   * Controls pagination state.
   */
  pagination: {
    /**
     * The index of the current page (zero-based).
     */
    pageIndex: number;
    /**
     * The number of rows displayed per page.
     */
    pageSize: number;
    /**
     * The total number of rows available in the dataset.
     */
    totalCount: number;
  };
  /**
   * Callback function for pagination changes.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/pagination)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  /**
   * Enables manual pagination when `true`.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/pagination#manualpagination)
   */
  manualPagination?: boolean;
  /**
   * Sorting runs on the server: the table only reports `sorting` and leaves the rows in the order
   * they came. Use with `sorting` / `onSortingChange` (see `useServerTable`).
   */
  manualSorting?: boolean;
  /**
   * Column filters run on the server: the table only reports `columnFilters` and shows every row it
   * got. Use with `columnFilters` / `onColumnFiltersChange` (see `useServerTable`).
   */
  manualFiltering?: boolean;
  /** Controlled sorting; the table keeps its own when left out. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Controlled column filters; the table keeps its own when left out. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * Used to configure which columns should be pinned to either the left or right side of the table.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/column-pinning)
   */
  columnPinningConfig?: ColumnPinningState;
  /**
   * Extra classes for the `<table>` element, e.g. a `min-w-*` so wide tables scroll horizontally
   * on narrow screens instead of squeezing every column.
   */
  tableClassName?: string;
  /**
   * A stable id per row (e.g. its ItemId). Row selection is keyed by it, so a selection stays on its
   * rows across server-side pages instead of following the row index.
   */
  getRowId?: (row: TData) => string;
  /** Which rows may be selected (others get a disabled checkbox). All rows when left out. */
  canSelectRow?: (row: TData) => boolean;
  /**
   * Clear the selection when the page changes. With server-side paging only the current page's rows
   * are known, so a selection carried across pages couldn't be acted on.
   */
  clearSelectionOnPageChange?: boolean;
  /**
   * How rows show on a phone (below 768px): `'table'` (the default) scrolls sideways, `'cards'`
   * shows each row as a card (see `DataCardList`; a column's `meta.mobile` places it on the card).
   */
  mobileView?: 'table' | 'cards';
  /** Extra classes for a row's card in `'cards'` view (e.g. a highlighted day). */
  getRowClassName?: (row: TData) => string | undefined;
  /** In `'cards'` view, the page's actions on the filter button's line (actions left, filter right). */
  mobileActions?: React.ReactNode;
  /** In `'cards'` view, an element to render the filter button into instead (e.g. in the page header). */
  mobileFilterSlot?: HTMLElement | null;
  /**
   * A totals row under the rows (Component Map › Tables): each column's total by column id; the first
   * data column says "Total" unless given its own. Shown on the table, not in `'cards'` view.
   */
  totals?: Partial<Record<string, React.ReactNode>>;
  /** Leaves out the footer (range, density, pager): for a short table that is one of several on a page. */
  hidePager?: boolean;
  /** Columns that start hidden, by id (`{ PhoneNumber: false }`), for optional columns. */
  defaultColumnVisibility?: VisibilityState;
  /** Remembers the Columns menu choice in this browser under this key. */
  columnVisibilityKey?: string;
  /**
   * Shows the rows as cards instead of the table (prototype Cards view): the card is drawn inside the same
   * white container, so the toolbar and pager keep their place. Phones use mobileView instead.
   */
  renderCards?: (rows: Row<TData>[]) => React.ReactNode;
}

function AdvanceDataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  isLoading = false,
  error = null,
  columnsToolbar,
  filterToolbar,
  isExpandRowContent = true,
  expandRowContent,
  pagination,
  onPaginationChange,
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  columnPinningConfig = DEFAULT_COLUMN_PINNING_CONFIG,
  tableClassName,
  getRowId,
  canSelectRow,
  clearSelectionOnPageChange = false,
  mobileView = 'table',
  getRowClassName,
  mobileActions,
  mobileFilterSlot,
  totals,
  hidePager = false,
  defaultColumnVisibility,
  columnVisibilityKey,
  renderCards,
}: Readonly<AdvanceDataTableProps<TData, TValue>>) {
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    initialColumnVisibility(defaultColumnVisibility, columnVisibilityKey)
  );
  useEffect(() => {
    if (columnVisibilityKey) storeColumnVisibility(columnVisibilityKey, columnVisibility);
  }, [columnVisibilityKey, columnVisibility]);
  const [ownColumnFilters, setOwnColumnFilters] = useState<ColumnFiltersState>([]);
  const [ownSorting, setOwnSorting] = useState<SortingState>([]);
  const columnFilters = controlledColumnFilters ?? ownColumnFilters;
  const sorting = controlledSorting ?? ownSorting;
  const [expanded, setExpanded] = useState({});
  const isMobile = useIsMobile();
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(columnPinningConfig);
  const { t } = useTranslation();
  const [density] = useTableDensity();
  const [tableElement, setTableElement] = useState<HTMLTableElement | null>(null);
  const overflows = useHorizontalOverflow(tableElement);

  useEffect(() => {
    setColumnPinning(() => ({
      left: isMobile ? ['select'] : columnPinningConfig.left,
      right: columnPinningConfig.right,
    }));
  }, [isMobile, columnPinningConfig]);

  // `meta.cardOnly` columns are part of the phone card only, never of the table.
  const isCardMode = isMobile && mobileView === 'cards';
  const visibility = useMemo(() => {
    if (isCardMode) return columnVisibility;
    const cardOnly = columns
      .filter((column) => column.meta?.cardOnly && column.id)
      .map((column) => [column.id as string, false] as const);
    return { ...columnVisibility, ...Object.fromEntries(cardOnly) };
  }, [isCardMode, columns, columnVisibility]);

  const table = useReactTable({
    data: error ? [] : data,
    columns,
    state: {
      sorting,
      columnVisibility: visibility,
      rowSelection,
      columnFilters,
      expanded,
      columnPinning,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    onExpandedChange: setExpanded,
    manualPagination,
    manualSorting,
    manualFiltering,
    defaultColumn: {
      filterFn: (row, columnId, filterValue: ColumnFilterValue) =>
        matchesColumnFilter(row.getValue(columnId), filterValue),
    },
    pageCount: Math.ceil(pagination.totalCount / pagination.pageSize),
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        });
        onPaginationChange?.(newPagination);
      } else {
        onPaginationChange?.(updater);
      }
    },
    enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : true,
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
    enableGrouping: true,
    groupedColumnMode: 'reorder',
    onRowSelectionChange: setRowSelection,
    onSortingChange: onSortingChange ?? setOwnSorting,
    onColumnFiltersChange: onColumnFiltersChange ?? setOwnColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnPinningChange: setColumnPinning,
    getGroupedRowModel: getGroupedRowModel(),
  });

  useEffect(() => {
    if (clearSelectionOnPageChange) setRowSelection({});
  }, [clearSelectionOnPageChange, pagination.pageIndex, pagination.pageSize]);

  // Numeric columns: `meta.numeric`, or every shown value is a number (amounts, hours, days, %).
  const pageRows = table.getRowModel().rows;
  const numericColumnIds = useMemo(() => {
    const ids = new Set<string>();
    table.getVisibleLeafColumns().forEach((column) => {
      const meta = column.columnDef.meta as { numeric?: boolean } | undefined;
      if (meta?.numeric !== undefined) {
        if (meta.numeric) ids.add(column.id);
        return;
      }
      if (column.id === 'select' || column.id === 'actions' || !column.accessorFn) return;
      let seen = 0;
      for (const row of pageRows) {
        const value = row.getValue(column.id);
        if (value === null || value === undefined || value === '' || value === '—') continue;
        if (!isNumericValue(value)) return;
        seen += 1;
      }
      if (seen > 0) ids.add(column.id);
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRows, columns, visibility]);
  const numericClass = (column: Column<TData, unknown>) =>
    numericColumnIds.has(column.id) && 'text-right tabular-nums [&>div]:justify-end';

  const renderSkeletonRows = () => {
    return Array.from({ length: pagination.pageSize }).map(() => (
      <TableRow key={`skeleton-${uuidv4()}`}>
        {columns.map(() => (
          <TableCell key={`skeleton-cell-${uuidv4()}`}>
            <Skeleton className="h-4 w-3/4" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // The cell spans every column, so on a narrow screen the table scrolls sideways and a centred
  // message would sit half off-screen. Pin it to the left edge at the scroll container's visible
  // width (`cqw`, see the container on <Table> below) so it stays centred in view.
  const renderMessageRow = (message: React.ReactNode, className?: string) => (
    <TableRow>
      <TableCell colSpan={columns.length} className={cn('h-24 p-0 text-center', className)}>
        <div className="sticky left-0 w-[100cqw] break-words px-4 py-2">{message}</div>
      </TableCell>
    </TableRow>
  );

  const renderErrorRow = (error: Error | null) =>
    renderMessageRow(
      error ? `${t('ERROR_LOADING_DATA')} ${error.message}` : t('UNKNOWN_ERROR_OCCURRED'),
      'text-destructive'
    );

  // TBL-06: when the column filters narrowed the list to nothing, offer to clear them.
  const renderNoResultFound = () =>
    renderMessageRow(
      table.getState().columnFilters.length > 0 ? (
        <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          {t('NO_RESULTS_FOUND')}
          <Button type="button" variant="link" size="sm" onClick={() => table.resetColumnFilters()}>
            {t('CLEAR_FILTERS')}
          </Button>
        </span>
      ) : (
        t('NO_RESULTS_FOUND')
      ),
      'text-muted-foreground'
    );

  const renderTableBodyContent = () => {
    if (isLoading) return renderSkeletonRows();
    if (error) return renderErrorRow(error);
    if (!table.getRowModel().rows.length) return renderNoResultFound();

    return table.getRowModel().rows.map((row) => (
      <React.Fragment key={row.id}>
        {renderTableRow(row)}
        {isExpandRowContent && row.getIsExpanded() && expandRowContent?.(row.id, columns.length)}
      </React.Fragment>
    ));
  };

  const renderTableRow = (row: Row<TData>) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      onClick={() => onRowClick?.(row.original)}
      className={cn(
        // Hairline rows; hover and selection tint the row with the accent colour.
        'group/row border-b border-border hover:bg-accent',
        row.getIsSelected() ? '!bg-accent' : onRowClick && 'cursor-pointer',
        getRowClassName?.(row.original)
      )}
    >
      {row.getVisibleCells().map((cell) => renderTableCell(cell, row))}
    </TableRow>
  );

  const renderTableCell = (cell: Cell<TData, unknown>, row: Row<TData>) => {
    const { column } = cell;
    return (
      <TableCell
        key={cell.id}
        className={cn(
          // Component Map › Tables: the row height follows the density; cells stay on one line.
          'whitespace-nowrap px-2.5 first:pl-4 last:pr-4',
          DENSITY_CELL_CLASS[density],
          row.getIsSelected() && '!bg-accent',
          getCommonPinningClasses(column),
          getFirstColumnClasses(column),
          getControlColumnClasses(column),
          numericClass(column)
        )}
        style={{
          left: column.getIsPinned() === 'left' ? `${column.getStart('left')}px` : undefined,
          right: column.getIsPinned() === 'right' ? `${column.getAfter('right')}px` : undefined,
          width: getColumnWidth(column),
        }}
        onClick={cell.column.id === 'actions' ? (e) => e.stopPropagation() : undefined}
      >
        {cell.column.id === 'select'
          ? renderSelectCell(row)
          : flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  };

  const renderSelectCell = (row: Row<TData>) => (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        onClick={(e) => e.stopPropagation()}
        aria-label={t('SELECT_ROW')}
        className="border-muted-foreground data-[state=checked]:border-none border-2"
      />
      {isExpandRowContent && (
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
        >
          {row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
        </Button>
      )}
    </div>
  );

  const getCommonPinningClasses = (column: Column<TData, unknown>) => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

    return clsx(
      isPinned ? 'sticky z-raised bg-card' : 'relative z-base',
      isLastLeftPinnedColumn && 'shadow-inset-right',
      isFirstRightPinnedColumn && 'shadow-inset-left'
    );
  };

  // On phones the table is wider than the screen and scrolls sideways, but table auto-layout would
  // still squeeze the first data column to one word per line. Keep it readable.
  const firstDataColumnId = table
    .getVisibleLeafColumns()
    .find((column) => column.id !== 'select')?.id;
  // A table wider than its frame scrolls inside it with the first column frozen (Component Map ›
  // Tables); with a selection column, that column is the pinned one instead.
  const hasSelectColumn = table.getVisibleLeafColumns().some((column) => column.id === 'select');
  const getFirstColumnClasses = (column: Column<TData, unknown>) =>
    column.id === firstDataColumnId &&
    clsx(
      'max-sm:min-w-[9rem]',
      overflows &&
        !hasSelectColumn &&
        'sticky left-0 z-raised bg-card shadow-[inset_-1px_0_0_oklch(var(--border))]'
    );

  // The select and actions columns shrink to their controls (the actions sit at the right edge),
  // leaving the width to the data columns.
  const isActionsColumn = (column: Column<TData, unknown>) => column.id === 'actions';
  const isSelectColumn = (column: Column<TData, unknown>) => column.id === 'select';
  const getColumnWidth = (column: Column<TData, unknown>) =>
    isActionsColumn(column) || isSelectColumn(column) ? undefined : column.getSize();
  const getControlColumnClasses = (column: Column<TData, unknown>) =>
    clsx(
      isActionsColumn(column) && 'w-px whitespace-nowrap pr-4 text-right [&>div]:justify-end',
      isSelectColumn(column) && 'w-px whitespace-nowrap pr-4'
    );

  // No pager under a "no results" or error message; kept while loading so the page doesn't jump, and on
  // a later page that came back empty (e.g. its last row was just removed) so the user can go back.
  const hasNoRows =
    !isLoading &&
    (Boolean(error) ||
      (table.getRowModel().rows.length === 0 && table.getState().pagination.pageIndex === 0));
  const pager = hasNoRows || hidePager ? null : (
    <DataTablePagination
      showSelectedRowContent={false}
      table={table}
      totalCount={pagination.totalCount}
      onPaginationChange={onPaginationChange}
    />
  );

  if (isMobile && mobileView === 'cards') {
    let message: { text: string; isError?: boolean } | undefined;
    if (error) message = { text: `${t('ERROR_LOADING_DATA')} ${error.message}`, isError: true };
    else if (!table.getRowModel().rows.length) {
      // "No results" when a filter narrowed the list away, "No Data Found" when there is nothing at all.
      const isFiltered = table.getState().columnFilters.length > 0;
      message = { text: t(isFiltered ? 'NO_RESULTS_FOUND' : 'NO_DATA_FOUND') };
    }
    return (
      <div className="flex w-full flex-col gap-5">
        {columnsToolbar ? columnsToolbar(table) : null}
        <DataCardList
          table={table}
          isLoading={isLoading}
          message={message}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          actions={mobileActions}
          filterSlot={mobileFilterSlot}
          filterToolbar={filterToolbar ? filterToolbar(table) : null}
          renderExpanded={
            isExpandRowContent && expandRowContent
              ? (row) => expandRowContent(row.id, 1)
              : undefined
          }
        />
        {pager}
      </div>
    );
  }

  // Nothing to list: just the message, no empty table. While a column filter is on, the header stays,
  // as the filters are in it and the user needs them to widen the search again.
  const toolbar = columnsToolbar ? (
    <div className="border-b px-4 py-3 max-md:px-3 max-md:py-2.5">{columnsToolbar(table)}</div>
  ) : null;

  if (renderCards && !isMobile) {
    const cardRows = table.getRowModel().rows;
    return (
      <div className={TABLE_CARD_CLASS}>
        {toolbar}
        {isLoading ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-px p-px">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-36 rounded-none" />
            ))}
          </div>
        ) : error || cardRows.length === 0 ? (
          <Empty className="py-12">
            <EmptyHeader>
              <EmptyTitle>
                {error
                  ? `${t('ERROR_LOADING_DATA')} ${error.message}`
                  : t(
                      table.getState().columnFilters.length > 0
                        ? 'NO_RESULTS_FOUND'
                        : 'NO_DATA_FOUND'
                    )}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : (
          renderCards(cardRows)
        )}
        {pager}
      </div>
    );
  }
  if (hasNoRows && !error && table.getState().columnFilters.length === 0) {
    return (
      <div className={TABLE_CARD_CLASS}>
        {toolbar}
        {/* Component Map › Empty states. */}
        <Empty className="py-12">
          <EmptyHeader>
            <EmptyTitle>{t('NO_DATA_FOUND')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    // HR Module Prototype `.tcard`: one white card holds the toolbar, the rows and the pager, with a white
    // header over a hairline. Inside a card, dialog or sheet the table stays flat.
    <div className={TABLE_CARD_CLASS}>
      {toolbar}
      <Table
        ref={setTableElement}
        className={tableClassName}
        // A table that fits keeps the page scroll, so its header pins under the top bar; one wider than
        // its frame scrolls inside it with the first column frozen.
        divClassname={cn(
          '[container-type:inline-size]',
          !overflows && 'overflow-visible overflow-x-visible overflow-y-visible'
        )}
      >
        <TableHeader className="[&_tr]:border-b-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-0 hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const { column } = header;
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      // The divider is an inset shadow so it stays with the sticky header.
                      'h-9 whitespace-nowrap bg-card px-2.5 text-[13px] font-medium text-muted-foreground shadow-[inset_0_-1px_0_oklch(var(--border))] first:pl-4 last:pr-4',
                      !overflows && 'sticky top-0 z-sticky',
                      getCommonPinningClasses(column),
                      getFirstColumnClasses(column),
                      getControlColumnClasses(column),
                      numericClass(column)
                    )}
                    style={{
                      left:
                        column.getIsPinned() === 'left'
                          ? `${column.getStart('left')}px`
                          : undefined,
                      right:
                        column.getIsPinned() === 'right'
                          ? `${column.getAfter('right')}px`
                          : undefined,
                      width: getColumnWidth(column),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
          {filterToolbar ? filterToolbar(table) : null}
        </TableHeader>
        <TableBody>{renderTableBodyContent()}</TableBody>
        {totals && !isLoading && !hasNoRows && (
          <TableFooter className="border-t border-border bg-surface-subtle font-semibold">
            <TableRow className="hover:bg-transparent">
              {table.getVisibleLeafColumns().map((column) => (
                <TableCell
                  key={column.id}
                  className={cn(
                    'whitespace-nowrap px-2.5 first:pl-4 last:pr-4',
                    DENSITY_CELL_CLASS[density],
                    getCommonPinningClasses(column),
                    getFirstColumnClasses(column),
                    getControlColumnClasses(column),
                    numericClass(column)
                  )}
                  style={{ width: getColumnWidth(column) }}
                >
                  {totals[column.id] ?? (column.id === firstDataColumnId ? t('TOTAL') : null)}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        )}
      </Table>
      {pager && <div className="-mt-px border-t px-4 py-2.5 max-md:px-3">{pager}</div>}
    </div>
  );
}
export default AdvanceDataTable;
